import type { FetchFunction } from '#layers/base/utils/api-fetch';
import { joinURL } from 'ufo';
import { useRuntimeConfig } from '#app/nuxt';
import { createApiFetch } from '#layers/base/utils/api-fetch';

const apiFetch = createApiFetch();

/**
 * Returns a fetch function bound to one immutable workspace base URL.
 * Either the runtime or the caller can cancel a request; a lone signal is passed through by identity.
 */
export function createWorkspaceFetch({ baseURL, runtimeSignal }: { baseURL: string; runtimeSignal?: AbortSignal }): FetchFunction {
	return (endpoint, options) => {
		const signals = [...new Set([runtimeSignal, options?.signal].filter((signal) => !!signal))];

		return apiFetch(endpoint, {
			...options,
			baseURL,
			signal: signals.length > 1 ? AbortSignal.any(signals) : signals[0],
		}) as Promise<any>;
	};
}

/** Captures runtime config during setup and resolves API URLs for explicit workspace slugs later. */
export function useWorkspaceApiBaseUrlFactory(): (workspaceSlug: string) => string {
	const runtimeConfig = useRuntimeConfig();

	return (workspaceSlug) => joinURL(runtimeConfig.public.api, workspaceSlug);
}

/** Creates immutable transports for explicitly named workspaces. */
export function useWorkspaceFetchFactory(): (options: { signal?: AbortSignal; workspaceSlug: string }) => FetchFunction {
	const createBaseUrl = useWorkspaceApiBaseUrlFactory();

	return ({ signal, workspaceSlug }) => createWorkspaceFetch({
		baseURL: createBaseUrl(workspaceSlug),
		runtimeSignal: signal,
	});
}
